import {
  CAMPAIGN_SANDBOX_ROLE_DM,
  CAMPAIGN_SANDBOX_ROLE_PLAYER,
  CAMPAIGN_SANDBOX_SOURCE_BLANK,
  CAMPAIGN_SANDBOX_SOURCE_CAMPAIGN,
  CAMPAIGN_SANDBOX_SOURCE_DEMO,
  CAMPAIGN_SANDBOX_SOURCE_FORGE_PREVIEW,
  RUNTIME_SCOPE_CAMPAIGN_SANDBOX,
  SANDBOX_AUTHORITY_FIELD_KEYS,
  STORAGE_MODE_LOCAL_SANDBOX,
  buildCampaignSandboxInitialLaunch,
  buildCampaignSandboxLaunchTarget,
  buildSandboxCampaignId,
  normalizeCampaignSandboxSeedId,
  normalizeCampaignSandboxSourceMode,
  normalizeSandboxSegment,
} from './campaignSandboxContract';
import {
  getEffectiveCampaignFeatures,
  getEffectiveCampaignHomebrew,
} from './campaignFeatureAccess';

// Re-exported for existing consumers/tests; the canonical definitions live in
// campaignSandboxContract.js so the seed builders, the local driver, and the
// sandbox storage layer can never drift apart.
export {
  SANDBOX_AUTHORITY_FIELD_KEYS,
  normalizeCampaignSandboxSeedId,
  normalizeSandboxSegment,
};

export const SANDBOX_DEMO_PRESET_CATEGORIES = Object.freeze({
  systems: true,
  items: true,
  crafting: true,
  professions: true,
  buffsDebuffs: true,
  combat: true,
});

export const CAMPAIGN_SANDBOX_DEMO_PERSONAS = Object.freeze([
  {
    presetKey: 'lika',
    name: "Li'ka",
    race: 'Half-Elf',
    charClass: 'Cleric',
    level: 5,
    maxHp: 42,
    currentHp: 38,
    maxMana: 12,
    currentMana: 8,
    armorClass: 15,
    speed: 35,
    initiative: 3,
    baseStats: { str: 14, dex: 16, con: 14, int: 12, wis: 15, cha: 10 },
    skillProficiencies: ['Athletics', 'Perception', 'Stealth', 'Survival', 'Nature'],
    skillExpertise: ['Survival'],
    savingThrowProficiencies: ['str', 'dex'],
    buffs: ['+2 Survival', 'Bless (self)'],
    debuffs: ['Observed', 'Exhaustion 1', '-1 Str'],
    permanentBuffs: ['Darkvision', 'Fey Ancestry', 'Divine Domain: Life'],
    permanentDebuffs: ['Chained'],
    inventory: [
      { name: 'Healing Potion', type: 'consumable', quantity: 2 },
      { name: 'Blessed Mace', type: 'weapon', quantity: 1, equipped: true },
    ],
    wallet: { copper: 5, silver: 10, gold: 100, platinum: 0 },
  },
  {
    presetKey: 'ventyl',
    name: 'Ventyl',
    race: 'Half-Elf',
    charClass: 'Ranger',
    level: 5,
    maxHp: 42,
    currentHp: 38,
    maxMana: 12,
    currentMana: 8,
    armorClass: 16,
    speed: 30,
    initiative: 3,
    baseStats: { str: 13, dex: 16, con: 14, int: 11, wis: 15, cha: 10 },
    skillProficiencies: ['Animal Handling', 'Nature', 'Perception', 'Stealth', 'Survival'],
    skillExpertise: [],
    savingThrowProficiencies: ['str', 'dex'],
    buffs: ['+1 Perception', '+2 Damage'],
    debuffs: ['-1 Dex'],
    permanentBuffs: ['+2 Survival'],
    permanentDebuffs: [],
    inventory: [
      { name: 'Phantom Greatsword', type: 'weapon', quantity: 1, equipped: true },
      { name: 'Traveler Rations', type: 'food', quantity: 4 },
    ],
    wallet: { copper: 0, silver: 18, gold: 42, platinum: 0 },
  },
  {
    presetKey: 'eve',
    name: 'Eve',
    race: 'Human',
    charClass: 'Ranger',
    level: 5,
    maxHp: 38,
    currentHp: 32,
    maxMana: 20,
    currentMana: 16,
    armorClass: 18,
    speed: 30,
    initiative: 1,
    baseStats: { str: 14, dex: 10, con: 14, int: 12, wis: 17, cha: 13 },
    skillProficiencies: ['Insight', 'Medicine', 'Persuasion', 'Religion'],
    skillExpertise: [],
    savingThrowProficiencies: ['wis', 'cha'],
    buffs: ["Hunter's Mark"],
    debuffs: [],
    permanentBuffs: ['Fighting Style: Archery', 'Natural Explorer'],
    permanentDebuffs: [],
    inventory: [
      { name: 'Longbow', type: 'weapon', quantity: 1, equipped: true },
      { name: 'Shield', type: 'armor', quantity: 1, equipped: true },
    ],
    wallet: { copper: 2, silver: 5, gold: 65, platinum: 0 },
  },
  {
    presetKey: 'khan',
    name: 'Khan',
    race: 'Tiefling',
    charClass: 'Rogue',
    level: 5,
    maxHp: 32,
    currentHp: 28,
    maxMana: 8,
    currentMana: 6,
    armorClass: 16,
    speed: 30,
    initiative: 5,
    baseStats: { str: 8, dex: 18, con: 12, int: 14, wis: 10, cha: 16 },
    skillProficiencies: ['Acrobatics', 'Deception', 'Investigation', 'Sleight of Hand', 'Stealth'],
    skillExpertise: ['Stealth', 'Sleight of Hand'],
    savingThrowProficiencies: ['dex', 'int'],
    buffs: ['Sneak Attack Ready'],
    debuffs: ['Marked by Debt'],
    permanentBuffs: ['Infernal Legacy', 'Thieves Cant'],
    permanentDebuffs: [],
    inventory: [
      { name: 'Dagger', type: 'weapon', quantity: 2, equipped: true },
      { name: 'Thieves Tools', type: 'tool', quantity: 1 },
    ],
    wallet: { copper: 20, silver: 7, gold: 19, platinum: 0 },
  },
]);

const AUTHORITY_FIELD_SET = new Set(SANDBOX_AUTHORITY_FIELD_KEYS);

const RUNTIME_ID_KEYS = new Set([
  'id',
  'charId',
  'sourceCharId',
  'targetCharId',
  'entryId',
  'instanceId',
  'itemEntryId',
  'sourceItemEntryId',
  'destinationItemEntryId',
  'effectInstanceId',
  'combatantId',
  'claimId',
  'dropId',
  'offerId',
  'transactionId',
]);

const FEATURE_GATED_PRESET_CATEGORIES = Object.freeze({
  systems: ['systems'],
  crafting: ['crafting'],
  professions: ['crafting', 'professions'],
  combat: ['combat'],
});

const EMPTY_COMBAT = Object.freeze({
  active: false,
  combatants: [],
  enemies: [],
  round: 1,
  turnIndex: 0,
  damageLog: [],
});

const DEFAULT_AUDIT = Object.freeze({
  activeSessionId: null,
  sessions: {},
  events: {},
  snapshots: {},
});

export const buildCampaignSandboxCharacterId = ({
  sandboxId = 'default',
  personaKey = null,
  sourceLabel = null,
  index = 0,
} = {}) => {
  const normalizedSandboxId = normalizeCampaignSandboxSeedId(sandboxId);
  const key = normalizeSandboxSegment(personaKey || sourceLabel || `char-${index + 1}`, `char-${index + 1}`);
  return `sandbox-${normalizedSandboxId}-persona-${key}`;
};

export const normalizeSandboxPresetCategories = (
  categories = SANDBOX_DEMO_PRESET_CATEGORIES,
  features = null,
) => {
  const effectiveFeatures = getEffectiveCampaignFeatures(features);
  const raw = categories && typeof categories === 'object' ? categories : {};
  const out = Object.fromEntries(
    Object.keys(SANDBOX_DEMO_PRESET_CATEGORIES).map((key) => [
      key,
      Object.prototype.hasOwnProperty.call(raw, key)
        ? raw[key] === true
        : SANDBOX_DEMO_PRESET_CATEGORIES[key],
    ]),
  );

  Object.entries(FEATURE_GATED_PRESET_CATEGORIES).forEach(([category, gates]) => {
    if (!gates.every((featureKey) => effectiveFeatures[featureKey] !== false)) {
      out[category] = false;
    }
  });

  return out;
};

const cloneJson = (value) => JSON.parse(JSON.stringify(value));

const isPlainObject = (value) => (
  value && typeof value === 'object' && !Array.isArray(value)
);

const clampCount = (value, max, fallback = max) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, Math.min(max, Math.trunc(parsed)));
};

const sanitizeRuntimeValue = (value, path = [], idPrefix = 'sandbox', sandboxCampaignId = null) => {
  if (Array.isArray(value)) {
    return value.map((item, index) => sanitizeRuntimeValue(item, [...path, String(index)], idPrefix, sandboxCampaignId));
  }
  if (!isPlainObject(value)) return value;

  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => !AUTHORITY_FIELD_SET.has(key))
      .map(([key, childValue]) => {
        if (RUNTIME_ID_KEYS.has(key) && typeof childValue === 'string') {
          return [key, `${idPrefix}-${normalizeSandboxSegment([...path, key, childValue].join('-'), key)}`];
        }
        if (key === 'campaignId') {
          // Every retargeted campaignId points at the ONE sandbox campaign id
          // (`sandbox-campaign-<sandboxId>`) so chars, nested provenance, and
          // globalSettings all agree. Per-character campaign ids (the old
          // `${idPrefix}-campaign` shape) broke char-to-campaign comparisons.
          return [key, sandboxCampaignId || `${idPrefix}-campaign`];
        }
        return [key, sanitizeRuntimeValue(childValue, [...path, key], idPrefix, sandboxCampaignId)];
      }),
  );
};

const buildInventory = (persona, charId, categories) => {
  if (!categories.items) return [];
  return (persona.inventory || []).map((item, index) => ({
    id: `${charId}-item-${index + 1}`,
    quantity: 1,
    ...cloneJson(item),
  }));
};

export const getCampaignSandboxDemoPersonaDefinition = (presetKey) => (
  CAMPAIGN_SANDBOX_DEMO_PERSONAS.find((persona) => persona.presetKey === presetKey) || null
);

const buildPersonaCharacter = (persona, {
  sandboxId = 'default',
  categories = SANDBOX_DEMO_PRESET_CATEGORIES,
  enabledFeatures = null,
  index = 0,
} = {}) => {
  const normalizedSandboxId = normalizeCampaignSandboxSeedId(sandboxId);
  const charId = buildCampaignSandboxCharacterId({
    sandboxId: normalizedSandboxId,
    personaKey: persona.presetKey,
    index,
  });
  const presetCategories = normalizeSandboxPresetCategories(categories, enabledFeatures);
  return {
    id: charId,
    name: persona.name,
    race: persona.race,
    charClass: persona.charClass,
    level: persona.level || 1,
    maxHp: persona.maxHp,
    currentHp: persona.currentHp ?? persona.maxHp,
    maxMana: persona.maxMana || 0,
    currentMana: persona.currentMana ?? persona.maxMana ?? 0,
    armorClass: persona.armorClass || 10,
    speed: persona.speed || 30,
    initiative: persona.initiative || 0,
    baseStats: cloneJson(persona.baseStats || {}),
    skillProficiencies: cloneJson(persona.skillProficiencies || []),
    skillExpertise: cloneJson(persona.skillExpertise || []),
    savingThrowProficiencies: cloneJson(persona.savingThrowProficiencies || []),
    buffList: presetCategories.buffsDebuffs ? cloneJson(persona.buffs || []) : [],
    debuffList: presetCategories.buffsDebuffs ? cloneJson(persona.debuffs || []) : [],
    permanentBuffs: presetCategories.buffsDebuffs ? cloneJson(persona.permanentBuffs || []) : [],
    permanentDebuffs: presetCategories.buffsDebuffs ? cloneJson(persona.permanentDebuffs || []) : [],
    buffSources: {},
    buffNotes: {},
    inventory: buildInventory(persona, charId, presetCategories),
    wallet: presetCategories.items ? cloneJson(persona.wallet || {}) : {},
    equipment: [],
    craftingTable: presetCategories.crafting ? [] : [],
    learnedRecipes: presetCategories.crafting ? [] : [],
    playerProfs: presetCategories.professions ? {} : {},
    systemList: presetCategories.systems ? [] : [],
    attacks: presetCategories.combat ? [] : [],
    spells: presetCategories.combat ? [] : [],
    spellSlots: presetCategories.combat ? { 1: 2 } : {},
    usedSpellSlots: {},
    diseaseList: [],
    diseaseImmunityList: [],
    actionHistory: [],
    statusHistory: [],
    campaignCharacterKind: 'sandbox',
    isSandboxCharacter: true,
    sandboxId: normalizedSandboxId,
    sandboxPresetKey: persona.presetKey,
    sandboxPresetCategories: presetCategories,
  };
};

export const buildCampaignSandboxDemoPersona = ({
  sandboxId = 'default',
  presetKey = 'lika',
  categories = SANDBOX_DEMO_PRESET_CATEGORIES,
  enabledFeatures = null,
  index = 0,
} = {}) => {
  const persona = getCampaignSandboxDemoPersonaDefinition(presetKey);
  if (!persona) return null;
  return buildPersonaCharacter(persona, {
    sandboxId,
    categories,
    enabledFeatures,
    index,
  });
};

export const buildCampaignSandboxBlankPersona = ({
  sandboxId = 'default',
  name = 'Sandbox Character',
  race = 'Unknown',
  charClass = 'Adventurer',
  maxHp = 10,
  categories = SANDBOX_DEMO_PRESET_CATEGORIES,
  enabledFeatures = null,
  index = 0,
} = {}) => {
  const normalizedSandboxId = normalizeCampaignSandboxSeedId(sandboxId);
  const displayName = typeof name === 'string' && name.trim()
    ? name.trim().slice(0, 80)
    : 'Sandbox Character';
  const charId = buildCampaignSandboxCharacterId({
    sandboxId: normalizedSandboxId,
    sourceLabel: displayName,
    index,
  });
  return {
    id: charId,
    name: displayName,
    race,
    charClass,
    level: 1,
    maxHp: Number.isFinite(Number(maxHp)) ? Number(maxHp) : 10,
    currentHp: Number.isFinite(Number(maxHp)) ? Number(maxHp) : 10,
    maxMana: 0,
    currentMana: 0,
    armorClass: 10,
    speed: 30,
    initiative: 0,
    baseStats: {},
    skillProficiencies: [],
    skillExpertise: [],
    savingThrowProficiencies: [],
    buffList: [],
    debuffList: [],
    permanentBuffs: [],
    permanentDebuffs: [],
    buffSources: {},
    buffNotes: {},
    inventory: [],
    wallet: {},
    equipment: [],
    craftingTable: [],
    learnedRecipes: [],
    playerProfs: {},
    systemList: [],
    attacks: [],
    spells: [],
    spellSlots: {},
    usedSpellSlots: {},
    diseaseList: [],
    diseaseImmunityList: [],
    actionHistory: [],
    statusHistory: [],
    campaignCharacterKind: 'sandbox',
    isSandboxCharacter: true,
    sandboxId: normalizedSandboxId,
    sandboxPresetCategories: normalizeSandboxPresetCategories(categories, enabledFeatures),
    sandboxSource: {
      kind: 'blank',
      label: displayName,
    },
  };
};

export const sanitizeSandboxCharacterSnapshot = (sourceCharacter, {
  sandboxId = 'default',
  index = 0,
  sourceLabel = null,
} = {}) => {
  const normalizedSandboxId = normalizeCampaignSandboxSeedId(sandboxId);
  const sourceName = sourceCharacter?.name || sourceLabel || `Character ${index + 1}`;
  const charId = buildCampaignSandboxCharacterId({
    sandboxId: normalizedSandboxId,
    sourceLabel: sourceName,
    index,
  });
  const idPrefix = `sandbox-${normalizedSandboxId}-${normalizeSandboxSegment(sourceName, `char-${index + 1}`)}`;
  const sandboxCampaignId = buildSandboxCampaignId(normalizedSandboxId);
  const sanitized = sanitizeRuntimeValue(cloneJson(sourceCharacter || {}), [], idPrefix, sandboxCampaignId);
  return {
    ...sanitized,
    id: charId,
    name: sourceName,
    campaignId: sandboxCampaignId,
    campaignCharacterKind: 'sandbox',
    isSandboxCharacter: true,
    sandboxId: normalizedSandboxId,
    sandboxSource: {
      kind: 'detachedCharacterSnapshot',
      label: sourceName,
    },
  };
};

const normalizeMetadata = ({
  sandboxId = 'default',
  sourceMode = CAMPAIGN_SANDBOX_SOURCE_BLANK,
  sourceCampaignId = null,
  name = 'Campaign Sandbox',
  tagline = '',
  baseThemeId = 'fantasy-dark',
  baseVariant = 'amethyst',
  rulesMode = '5e',
  enabledFeatures = null,
  homebrew = null,
  world = null,
  linkModes = null,
  moduleChoices = null,
  sandboxPresetCategories = null,
} = {}) => {
  const normalizedSandboxId = normalizeCampaignSandboxSeedId(sandboxId);
  const effectiveFeatures = getEffectiveCampaignFeatures(enabledFeatures);
  const effectiveHomebrew = getEffectiveCampaignHomebrew(homebrew);
  const effectiveSandboxPresetCategories = normalizeSandboxPresetCategories(
    sandboxPresetCategories || SANDBOX_DEMO_PRESET_CATEGORIES,
    effectiveFeatures,
  );
  return {
    sandboxId: normalizedSandboxId,
    sandboxCampaignId: buildSandboxCampaignId(normalizedSandboxId),
    sourceMode: normalizeCampaignSandboxSourceMode(sourceMode),
    sourceCampaignId: sourceCampaignId || null,
    name: typeof name === 'string' && name.trim() ? name.trim().slice(0, 120) : 'Campaign Sandbox',
    tagline: typeof tagline === 'string' ? tagline.trim().slice(0, 160) : '',
    baseThemeId: typeof baseThemeId === 'string' && baseThemeId.trim() ? baseThemeId.trim() : 'fantasy-dark',
    baseVariant: typeof baseVariant === 'string' && baseVariant.trim() ? baseVariant.trim() : 'amethyst',
    rulesMode: typeof rulesMode === 'string' && rulesMode.trim() ? rulesMode.trim() : '5e',
    enabledFeatures: effectiveFeatures,
    homebrew: effectiveHomebrew,
    world: world && typeof world === 'object' ? cloneJson(world) : null,
    linkModes: linkModes && typeof linkModes === 'object' ? cloneJson(linkModes) : {},
    moduleChoices: moduleChoices && typeof moduleChoices === 'object' ? cloneJson(moduleChoices) : {},
    sandboxPresetCategories: effectiveSandboxPresetCategories,
  };
};

const buildDefaults = (metadata) => ({
  diets: {},
  diseases: {},
  systems: {},
  foodTypes: [],
  locations: [],
  currentLocation: 'Stone Bridge',
  journal: [],
  npcLibrary: {},
  journalSettings: { showLinkTooltips: true },
  audit: cloneJson(DEFAULT_AUDIT),
  globalSettings: {
    campaignSandbox: true,
    campaignId: metadata.sandboxCampaignId,
    campaignName: metadata.name,
    campaignTagline: metadata.tagline,
    baseThemeId: metadata.baseThemeId,
    baseVariant: metadata.baseVariant,
    rulesMode: metadata.rulesMode,
    enabledFeatures: cloneJson(metadata.enabledFeatures),
    homebrew: cloneJson(metadata.homebrew),
    sandboxPresetCategories: cloneJson(metadata.sandboxPresetCategories),
    world: metadata.world,
    linkModes: metadata.linkModes,
    moduleChoices: metadata.moduleChoices,
  },
});

const buildSeedEnvelope = ({
  metadata,
  chars = {},
  food = { totalUnits: 0, foodLog: [] },
  groupTravelLog = [],
  combat = EMPTY_COMBAT,
  sandboxRole = CAMPAIGN_SANDBOX_ROLE_DM,
  sandboxPersonaId = null,
  initialView = null,
} = {}) => {
  const defaults = buildDefaults(metadata);
  const demoData = {
    chars,
    food,
    groupTravelLog,
    combat,
  };
  const storageSeed = {
    defaults,
    demoData,
  };
  const launchTarget = {
    ...buildCampaignSandboxLaunchTarget({
    sandboxId: metadata.sandboxId,
    sourceCampaignId: metadata.sourceCampaignId,
    sourceMode: metadata.sourceMode,
    sandboxRole,
    sandboxPersonaId,
    initialView,
    enabledFeatures: metadata.enabledFeatures,
    homebrew: metadata.homebrew,
    }),
    sandboxLabel: metadata.name,
    sandboxMetadata: metadata,
    storageSeed,
  };
  const storageConfig = {
    runtimeScope: RUNTIME_SCOPE_CAMPAIGN_SANDBOX,
    storageMode: STORAGE_MODE_LOCAL_SANDBOX,
    sandboxId: metadata.sandboxId,
    sourceCampaignId: metadata.sourceCampaignId,
    sourceMode: metadata.sourceMode,
    sandboxLabel: metadata.name,
    initialLaunch: buildCampaignSandboxInitialLaunch(launchTarget),
  };
  return {
    sandboxId: metadata.sandboxId,
    sourceMode: metadata.sourceMode,
    sourceCampaignId: metadata.sourceCampaignId,
    metadata,
    launchTarget,
    initialLaunch: storageConfig.initialLaunch,
    storageConfig,
    storageSeed,
    demoData,
  };
};

export const buildBlankCampaignSandboxSeed = (options = {}) => {
  const metadata = normalizeMetadata({
    ...options,
    sourceMode: options.sourceMode || CAMPAIGN_SANDBOX_SOURCE_BLANK,
  });
  const chars = {};
  if (options.sandboxRole === CAMPAIGN_SANDBOX_ROLE_PLAYER && options.sandboxPersonaId) {
    const blankPersona = buildCampaignSandboxBlankPersona({
      sandboxId: metadata.sandboxId,
      name: options.sandboxPersonaName || 'Sandbox Character',
      categories: metadata.sandboxPresetCategories,
      enabledFeatures: metadata.enabledFeatures,
    });
    chars[options.sandboxPersonaId] = {
      ...blankPersona,
      id: options.sandboxPersonaId,
    };
  }
  return buildSeedEnvelope({
    metadata,
    chars,
    sandboxRole: options.sandboxRole,
    sandboxPersonaId: options.sandboxPersonaId,
    initialView: options.initialView,
  });
};

export const buildDemoCampaignSandboxSeed = ({
  sandboxId = 'default',
  personaCount = CAMPAIGN_SANDBOX_DEMO_PERSONAS.length,
  presetCategories = SANDBOX_DEMO_PRESET_CATEGORIES,
  enabledFeatures = null,
  homebrew = null,
  ...options
} = {}) => {
  const metadata = normalizeMetadata({
    ...options,
    sandboxId,
    sourceMode: options.sourceMode || CAMPAIGN_SANDBOX_SOURCE_DEMO,
    name: options.name || 'Demo Campaign Sandbox',
    enabledFeatures,
    homebrew,
    sandboxPresetCategories: presetCategories,
  });
  const categories = metadata.sandboxPresetCategories;
  const count = clampCount(personaCount, CAMPAIGN_SANDBOX_DEMO_PERSONAS.length);
  const chars = Object.fromEntries(
    CAMPAIGN_SANDBOX_DEMO_PERSONAS.slice(0, count).map((persona, index) => {
      const char = buildPersonaCharacter(persona, {
        sandboxId: metadata.sandboxId,
        categories,
        enabledFeatures: metadata.enabledFeatures,
        index,
      });
      return [char.id, char];
    }),
  );
  return buildSeedEnvelope({
    metadata,
    chars,
    food: metadata.homebrew.foodSettings
      ? { totalUnits: 24, foodLog: [] }
      : { totalUnits: 0, foodLog: [] },
    groupTravelLog: metadata.enabledFeatures.travelLog ? [] : [],
    combat: metadata.enabledFeatures.combat ? cloneJson(EMPTY_COMBAT) : cloneJson(EMPTY_COMBAT),
    sandboxRole: options.sandboxRole,
    sandboxPersonaId: options.sandboxPersonaId,
    initialView: options.initialView,
  });
};

const buildForgeWorldMetadata = (form = {}) => ({
  mode: form.worldMode || 'isolated',
  worldId: form.worldId || null,
  newWorldName: form.newWorldName || '',
  existingId: form.existingId || '',
  origin: form.origin || '',
  templateId: form.templateId || '',
  copyFromId: form.copyFromId || '',
  label: form.newWorldName || form.worldName || form.worldId || form.existingId || null,
});

export const buildForgePreviewCampaignSandboxSeed = (form = {}, options = {}) => {
  const presetCategories = form.testPresetCategories
    || form.testCharacterPresetCategories
    || SANDBOX_DEMO_PRESET_CATEGORIES;
  const metadata = normalizeMetadata({
    sandboxId: options.sandboxId || form.sandboxId || form.name || 'forge-preview',
    sourceMode: CAMPAIGN_SANDBOX_SOURCE_FORGE_PREVIEW,
    sourceCampaignId: null,
    name: form.name || options.name || 'Campaign Sandbox Preview',
    tagline: form.tagline || options.tagline || '',
    baseThemeId: form.baseTheme || form.baseThemeId || options.baseThemeId,
    baseVariant: form.baseVariant || options.baseVariant,
    rulesMode: form.rulesMode || options.rulesMode,
    enabledFeatures: form.features || options.enabledFeatures,
    homebrew: form.homebrew || options.homebrew,
    world: buildForgeWorldMetadata(form),
    linkModes: form.linkModes || {},
    moduleChoices: {
      origin: form.origin || '',
      templateId: form.templateId || '',
      copyFromId: form.copyFromId || '',
      daySchedule: form.daySchedule || {},
      scheduleTBD: form.scheduleTBD === true,
    },
  });
  return buildDemoCampaignSandboxSeed({
    ...options,
    sandboxId: metadata.sandboxId,
    sourceMode: CAMPAIGN_SANDBOX_SOURCE_FORGE_PREVIEW,
    name: metadata.name,
    tagline: metadata.tagline,
    baseThemeId: metadata.baseThemeId,
    baseVariant: metadata.baseVariant,
    rulesMode: metadata.rulesMode,
    enabledFeatures: metadata.enabledFeatures,
    homebrew: metadata.homebrew,
    world: metadata.world,
    linkModes: metadata.linkModes,
    moduleChoices: metadata.moduleChoices,
    personaCount: form.dummyCount || 0,
    presetCategories,
    sandboxPresetCategories: presetCategories,
  });
};

export const buildDetachedCampaignSandboxSeed = ({
  sandboxId = 'default',
  characters = [],
  enabledFeatures = null,
  homebrew = null,
  sourceCampaignId = null,
  ...options
} = {}) => {
  const metadata = normalizeMetadata({
    ...options,
    sandboxId,
    sourceMode: CAMPAIGN_SANDBOX_SOURCE_CAMPAIGN,
    sourceCampaignId,
    enabledFeatures,
    homebrew,
    name: options.name || 'Detached Campaign Sandbox',
  });
  const seenIds = new Map();
  const chars = Object.fromEntries(
    (Array.isArray(characters) ? characters : [])
      .filter(Boolean)
      .map((character, index) => {
        const sandboxChar = sanitizeSandboxCharacterSnapshot(character, {
          sandboxId: metadata.sandboxId,
          index,
        });
        const baseId = sandboxChar.id;
        const seen = seenIds.get(baseId) || 0;
        seenIds.set(baseId, seen + 1);
        if (seen > 0) {
          sandboxChar.id = `${baseId}-${seen + 1}`;
        }
        return [sandboxChar.id, sandboxChar];
      }),
  );
  return buildSeedEnvelope({
    metadata,
    chars,
    sandboxRole: options.sandboxRole,
    sandboxPersonaId: options.sandboxPersonaId,
    initialView: options.initialView,
  });
};
